import { useEffect, useMemo, useRef, useState, type MouseEvent } from 'react';
import { Box, IconButton, Divider, Popover, Tooltip, type SxProps, type Theme } from '@mui/material';
import DOMPurify from 'dompurify';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import StrikethroughSIcon from '@mui/icons-material/StrikethroughS';
import TitleIcon from '@mui/icons-material/Title';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import CodeIcon from '@mui/icons-material/Code';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import LinkIcon from '@mui/icons-material/Link';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import EmojiEmotionsIcon from '@mui/icons-material/EmojiEmotions';
import { useEditor, EditorContent, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import { useTranslation } from 'react-i18next';

/** Kuratierte Emoji-Schnellauswahl (kein schweres Lib-Set). */
const EMOJIS = [
  '😀', '😄', '😁', '😉', '😊', '😎', '🤔', '😅', '😂', '🙂',
  '👍', '👎', '👏', '🙌', '🙏', '💪', '✌️', '👌', '🤝', '👀',
  '✅', '❌', '⚠️', '❗', '❓', '⭐', '🔥', '💡', '📌', '📍',
  '🚀', '🎯', '📈', '📉', '📊', '🗓️', '⏰', '⏳', '🔧', '🛠️',
  '✏️', '📝', '📎', '📂', '🔒', '🔓', '🟢', '🟡', '🔴', '🔵',
];

function ToolbarButton({
  active, disabled, onClick, title, children,
}: {
  active?: boolean; disabled?: boolean; onClick: (e: MouseEvent<HTMLElement>) => void; title: string; children: React.ReactNode;
}) {
  return (
    <Tooltip title={title} arrow>
      <span>
        <IconButton
          size="small"
          disabled={disabled}
          onMouseDown={(e) => e.preventDefault()}
          onClick={onClick}
          sx={{
            color: active ? 'var(--pm-petrol-500, var(--rittec-petrol))' : 'var(--pm-fg-muted)',
            bgcolor: active ? 'var(--pm-bg-muted)' : 'transparent',
            borderRadius: 1,
          }}
        >
          {children}
        </IconButton>
      </span>
    </Tooltip>
  );
}

function Toolbar({ editor }: { editor: Editor }) {
  const { t } = useTranslation('common');
  const [emojiAnchor, setEmojiAnchor] = useState<HTMLElement | null>(null);

  const openEmoji = (e: MouseEvent<HTMLElement>) => setEmojiAnchor(e.currentTarget);
  const insertEmoji = (emoji: string) => {
    editor.chain().focus().insertContent(emoji).run();
    setEmojiAnchor(null);
  };
  const toggleLink = () => {
    const prev = editor.getAttributes('link').href as string | undefined;
    const url = window.prompt(t('richtext.linkPrompt', 'Link-URL (leer = entfernen):'), prev ?? '');
    if (url === null) return;
    if (url === '') { editor.chain().focus().extendMarkRange('link').unsetLink().run(); return; }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  const sep = <Divider orientation="vertical" flexItem sx={{ mx: 0.25, my: 0.5 }} />;

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.25, p: 0.5, borderBottom: '1px solid var(--pm-border, var(--pm-bg-muted))' }}>
      <ToolbarButton title={t('richtext.bold', 'Fett')} active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()}><FormatBoldIcon fontSize="small" /></ToolbarButton>
      <ToolbarButton title={t('richtext.italic', 'Kursiv')} active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()}><FormatItalicIcon fontSize="small" /></ToolbarButton>
      <ToolbarButton title={t('richtext.underline', 'Unterstrichen')} active={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()}><FormatUnderlinedIcon fontSize="small" /></ToolbarButton>
      <ToolbarButton title={t('richtext.strike', 'Durchgestrichen')} active={editor.isActive('strike')} onClick={() => editor.chain().focus().toggleStrike().run()}><StrikethroughSIcon fontSize="small" /></ToolbarButton>
      {sep}
      <ToolbarButton title={t('richtext.h2', 'Überschrift')} active={editor.isActive('heading', { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}><TitleIcon fontSize="small" /></ToolbarButton>
      <ToolbarButton title={t('richtext.bulletList', 'Aufzählung')} active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()}><FormatListBulletedIcon fontSize="small" /></ToolbarButton>
      <ToolbarButton title={t('richtext.orderedList', 'Nummerierte Liste')} active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()}><FormatListNumberedIcon fontSize="small" /></ToolbarButton>
      <ToolbarButton title={t('richtext.quote', 'Zitat')} active={editor.isActive('blockquote')} onClick={() => editor.chain().focus().toggleBlockquote().run()}><FormatQuoteIcon fontSize="small" /></ToolbarButton>
      <ToolbarButton title={t('richtext.code', 'Code')} active={editor.isActive('code')} onClick={() => editor.chain().focus().toggleCode().run()}><CodeIcon fontSize="small" /></ToolbarButton>
      {sep}
      <ToolbarButton title={t('richtext.alignLeft', 'Linksbündig')} active={editor.isActive({ textAlign: 'left' })} onClick={() => editor.chain().focus().setTextAlign('left').run()}><FormatAlignLeftIcon fontSize="small" /></ToolbarButton>
      <ToolbarButton title={t('richtext.alignCenter', 'Zentriert')} active={editor.isActive({ textAlign: 'center' })} onClick={() => editor.chain().focus().setTextAlign('center').run()}><FormatAlignCenterIcon fontSize="small" /></ToolbarButton>
      <ToolbarButton title={t('richtext.alignRight', 'Rechtsbündig')} active={editor.isActive({ textAlign: 'right' })} onClick={() => editor.chain().focus().setTextAlign('right').run()}><FormatAlignRightIcon fontSize="small" /></ToolbarButton>
      {sep}
      <ToolbarButton title={t('richtext.link', 'Link')} active={editor.isActive('link')} onClick={toggleLink}><LinkIcon fontSize="small" /></ToolbarButton>
      <ToolbarButton title={t('richtext.emoji', 'Emoji')} onClick={openEmoji}><EmojiEmotionsIcon fontSize="small" /></ToolbarButton>
      {sep}
      <ToolbarButton title={t('richtext.undo', 'Rückgängig')} disabled={!editor.can().undo()} onClick={() => editor.chain().focus().undo().run()}><UndoIcon fontSize="small" /></ToolbarButton>
      <ToolbarButton title={t('richtext.redo', 'Wiederholen')} disabled={!editor.can().redo()} onClick={() => editor.chain().focus().redo().run()}><RedoIcon fontSize="small" /></ToolbarButton>

      <Popover
        open={!!emojiAnchor}
        anchorEl={emojiAnchor}
        onClose={() => setEmojiAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(10, 1fr)', gap: 0.25, p: 1, maxWidth: 320 }}>
          {EMOJIS.map((e) => (
            <Box
              key={e}
              role="button"
              onClick={() => insertEmoji(e)}
              sx={{ cursor: 'pointer', fontSize: 20, textAlign: 'center', borderRadius: 1, p: 0.25, '&:hover': { bgcolor: 'var(--pm-bg-muted)' } }}
            >
              {e}
            </Box>
          ))}
        </Box>
      </Popover>
    </Box>
  );
}

/**
 * Rich-Text-Editor (tiptap) mit voller Formatierungs-Toolbar + Emoji-Auswahl.
 * Speichert/liefert HTML; das Rendern erfolgt sanitized über {@link RichTextView}.
 */
export function PmRichTextEditor({
  value, onChange, placeholder, minHeight = 160,
}: {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minHeight?: number;
}) {
  // letzter von uns emittierter HTML-Wert — verhindert Cursor-Reset bei Echo.
  const lastEmitted = useRef(value);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ link: { openOnClick: false, autolink: true } }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
    ],
    content: value || '',
    onUpdate: ({ editor }) => {
      const html = editor.isEmpty ? '' : editor.getHTML();
      lastEmitted.current = html;
      onChange(html);
    },
  });

  // Externer value-Wechsel (anderer Datensatz geöffnet) -> Inhalt setzen.
  useEffect(() => {
    if (!editor) return;
    if (value !== lastEmitted.current) {
      lastEmitted.current = value;
      editor.commands.setContent(value || '', { emitUpdate: false });
    }
  }, [value, editor]);

  return (
    <Box
      sx={{
        border: '1px solid var(--pm-border, var(--pm-bg-muted))',
        borderRadius: 1,
        bgcolor: 'var(--pm-bg-base)',
        '& .ProseMirror': {
          minHeight, p: 1.25, outline: 'none', color: 'var(--pm-fg-base)', fontSize: 14, lineHeight: 1.5,
        },
        '& .ProseMirror:focus': { outline: 'none' },
        '& .ProseMirror p.is-editor-empty:first-of-type::before': {
          content: 'attr(data-placeholder)', color: 'var(--pm-fg-subtle)', float: 'left', height: 0, pointerEvents: 'none',
        },
        '& .ProseMirror a': { color: 'var(--pm-info)', textDecoration: 'underline' },
        '& .ProseMirror blockquote': { borderLeft: '3px solid var(--pm-border, var(--pm-bg-muted))', pl: 1.5, color: 'var(--pm-fg-muted)' },
        '& .ProseMirror code': { bgcolor: 'var(--pm-bg-muted)', px: 0.5, borderRadius: 0.5, fontSize: 13 },
      }}
    >
      {editor && <Toolbar editor={editor} />}
      <EditorContent editor={editor} data-placeholder={placeholder} />
    </Box>
  );
}

/** True, wenn das HTML nach Strip von Tags/Whitespace keinen sichtbaren Inhalt hat. */
export function isRichTextEmpty(html: string | null | undefined): boolean {
  if (!html) return true;
  return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim().length === 0;
}

/**
 * Rendert gespeichertes Rich-Text-HTML sicher (DOMPurify-Sanitize gegen XSS).
 * Gegenstueck zu {@link PmRichTextEditor}.
 */
/**
 * Formularelemente, die das HTML-Profil von DOMPurify zulaesst, hier aber nichts
 * verloren haben. Der Editor kann sie nicht erzeugen (das TipTap-Schema kennt sie
 * nicht) — ueber die pm-api liesse sich aber HTML mit einem echt aussehenden
 * Eingabefeld in eine Beschreibung schreiben, das andere Nutzer angezeigt bekommen.
 * Kein Skriptweg (`onclick`, `<script>` und `javascript:` fallen ohnehin), sondern
 * ein Irrefuehrungsweg. Befund von SMS PRO am 15.09.2026, an PHQ gegengemessen.
 */
const VERBOTENE_TAGS = ['form', 'input', 'button', 'select', 'option', 'textarea', 'label', 'fieldset', 'legend'];

export function RichTextView({ html, sx }: { html: string | null | undefined; sx?: SxProps<Theme> }) {
  const clean = useMemo(
    () => DOMPurify.sanitize(html ?? '', {
      USE_PROFILES: { html: true },
      ADD_ATTR: ['target', 'rel'],
      FORBID_TAGS: VERBOTENE_TAGS,
    }),
    [html],
  );
  if (isRichTextEmpty(html)) return null;
  return (
    <Box
      sx={{
        color: 'var(--pm-fg-muted)', fontSize: 14, lineHeight: 1.5,
        '& p': { m: 0, mb: 0.5 }, '& ul, & ol': { my: 0.5, pl: 2.5 },
        '& a': { color: 'var(--pm-info)', textDecoration: 'underline' },
        '& blockquote': { borderLeft: '3px solid var(--pm-border, var(--pm-bg-muted))', pl: 1.5, m: 0, color: 'var(--pm-fg-subtle)' },
        '& code': { bgcolor: 'var(--pm-bg-muted)', px: 0.5, borderRadius: 0.5, fontSize: 13 },
        ...sx,
      }}
      dangerouslySetInnerHTML={{ __html: clean }}
    />
  );
}
